import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { env } from '../environment';
import { RootState } from '../store';
import { User } from '../interfaces';

export const UserAPI = createApi({
  reducerPath: 'userAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: env.apiUrl + '/user',
    prepareHeaders: (headers, { getState }) => {
      const accessToken = (getState() as RootState).auth.accessToken;
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      return headers;
    }
  }),
  tagTypes: ['User'],
  endpoints: (build) => ({
    fetchUsers: build.query<User[], void>({
      query: () => '/',
      providesTags: result => result
        ? [...result.map(({ id }) => ({ type: 'User' as const, id }))]
        : ['User'],
    }),
    fetchUser: build.query<User, string>({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) =>
        result ? [{ type: 'User' as const, id }] : ['User'],
    }),
    createUser: build.mutation<User, Partial<User>>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    updateUser: build
      .mutation<User, { id: string; body: Partial<User> }>({
      query: ({ id, body }) => ({
        url: `/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        result ? [{ type: 'User' as const, id }] : ['User'],
    }),
    deleteUser: build.mutation<unknown, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) =>
        result ? [{ type: 'User' as const, id }] : ['User'],
    }),
  }),
});

export const {
  useFetchUsersQuery,
  useFetchUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = UserAPI;